import useLoginController from "@auth/api/useLoginController"
import redirects from "@services/redirects"
import { useAuthContext } from "@contexts/auth"
import { confirmation, notify, route } from "@helpers"
import { useCallback, useMemo } from "react"
import { useHistory } from "react-router"
import useSettingsController from "@customer/api/useSettingsController"
import useStatic from "./useStatic"
import UserMissingError from "@errors/UserMissingError"
import _ from "lodash"

const useAuth = <T extends Helpers.User = Helpers.User>() => {
    const controller = useLoginController()
    const settings = useSettingsController()
    const history = useHistory()
    const { data: user, setData: setUser } = useAuthContext()

    const isCustomer = useMemo(() => user?.role.name === "customer", [user])
    const isAdmin = useMemo(() => user?.role.name === "admin", [user])
    const isJury = useMemo(() => user?.role.name === "jury", [user])
    const hasDetails = useMemo(() => hasUserDetails(user), [user])

    const logout = useCallback(async () => {
        const user = await controller.logout()
        setUser(user || null)
        history.push(route("signin"))
        redirects.clear()
    }, [])

    const loginAsUser = useCallback(async (id: string | number) => {
        const user = await controller.loginAsUser(id)
        setUser(user)
        history.push(route("signin"))
        redirects.clear()
    }, [])

    const refreshUser = useCallback(async () => {
        const user = await controller.user.request()
        setUser(user || null)
    }, [])

    const destroy = useCallback(async () => {
        const confirmed = confirmation("Are you sure you want to delete the account? It will be impossible to restore the data.")
        if (!confirmed) return

        await settings.destroy()
        notify("The account was deleted. Thank you for being with us!")
        setUser(null)
    }, [])

    const set = useStatic((path: string, value: any) => {
        setUser(user => {
            if (!user)
                throw new UserMissingError()
                
            _.set(user, path, value)

            return user
        })
    })

    return { 
        user: (user as T), 
        setUser: (setUser as React.Dispatch<React.SetStateAction<T>>), 
        logout, 
        loginAsUser, 
        refreshUser, 
        destroy,
        hasDetails, 
        isCustomer, 
        isAdmin, 
        isJury,
        set
    }
}

export default useAuth

function hasUserDetails(user: Helpers.User | null | undefined): boolean {
    if (user?.role.name === "customer") {
        const customer = user as Resources.Customer | Resources.CustomerPartial

        return Boolean(
            customer?.email &&
            customer?.first_name &&
            customer?.last_name &&
            customer?.address && 
            customer?.country && 
            customer?.zip
        )
    }

    return true
}